import { fail } from './errors.js';

/* 센터 수업(프로그램 안내): 원장이 만들고 공개하면 보호자가 본다. 참여 아동 이름·개인정보는 담지 않는다. */

export const COURSE_FIELDS = ['LANGUAGE', 'SOCIAL', 'SENSORY', 'MOTOR', 'LEARNING'] as const;
export type CourseField = (typeof COURSE_FIELDS)[number];
export const COURSE_FIELD_LABEL: Record<CourseField, string> = { LANGUAGE: '언어', SOCIAL: '사회성', SENSORY: '감각', MOTOR: '운동', LEARNING: '학습' };

export const COURSE_MODES = ['GROUP', 'PAIR', 'INDIVIDUAL'] as const;
export type CourseMode = (typeof COURSE_MODES)[number];
export const COURSE_MODE_LABEL: Record<CourseMode, string> = { GROUP: '소그룹', PAIR: '짝 활동', INDIVIDUAL: '개별' };

export const COURSE_LIMITS = { name: 40, teacher: 30, audience: 40, schedule: 60, summary: 80, description: 500, materials: 80, capacityMax: 100, feeMax: 1_000_000, note: 200, reason: 200 } as const;

export interface CenterCourse {
  id: string;
  centerId: string;
  name: string;
  field: CourseField;
  mode: CourseMode;
  teacher: string;      // 담당 선생님 표시 이름
  audience: string;     // 참여 대상(안내 기준)
  schedule: string;     // 수업 시간 안내
  summary: string;      // 한 줄 소개
  description: string;  // 수업 소개
  materials: string;    // 준비물
  goals: string[];      // "이런 경험을 함께해요"
  capacity: number;
  enrolled: number;     // 현재 인원(숫자만)
  published: boolean;   // 보호자에게 공개
  signupOpen?: boolean; // 보호자 수강 신청을 받는다(없으면 받지 않음)
  fee?: number;         // 회당 수업료(원, 없으면 상담 후 안내)
  programId?: string;   // 센터 프로그램(프로그램 관리)에서 고른 경우
  sortOrder?: number;   // 원장이 정한 보이는 순서(작을수록 앞, 없으면 만든 순서로 뒤에)
  version: number;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export type CourseInput = Pick<CenterCourse, 'name' | 'field' | 'mode' | 'teacher' | 'audience' | 'schedule' | 'summary' | 'description' | 'materials' | 'capacity' | 'enrolled' | 'published'> & Pick<Partial<CenterCourse>, 'signupOpen' | 'fee' | 'programId'>;

/** 분야별 기본 목표(수업마다 따로 적지 않았을 때) */
export const DEFAULT_COURSE_GOALS: Record<CourseField, string[]> = {
  LANGUAGE: ['상황에 맞는 말로 생각을 표현해요', '친구와 차례를 지키며 이야기를 주고받아요', '그림책 속 이야기를 함께 나눠요'],
  SOCIAL: ['함께 정한 규칙을 지키며 놀이해요', '친구의 마음을 살펴보고 표현해요', '도움을 요청하고 도와주는 경험을 해요'],
  SENSORY: ['다양한 감각 재료를 편안하게 탐색해요', '몸의 균형과 움직임을 조절해요', '활동 사이 전환을 연습해요'],
  MOTOR: ['큰 움직임으로 몸의 협응을 경험해요', '손끝을 쓰는 활동에 집중해요', '끝까지 해내는 성취감을 느껴요'],
  LEARNING: ['활동 순서를 이해하고 따라가요', '주의를 모아 과제를 마무리해요', '스스로 해 본 부분을 돌아봐요'],
};

const text = (v: unknown, max: number, label: string, required = false) => {
  const s = typeof v === 'string' ? v.trim() : '';
  if (required && !s) fail('VALIDATION', `${label}을(를) 입력하세요.`);
  if (s.length > max) fail('VALIDATION', `${label}은(는) ${max}자 이하로 입력하세요.`);
  return s;
};
const int = (v: unknown, min: number, max: number, label: string) => {
  const n = Number(v);
  if (!Number.isInteger(n) || n < min || n > max) fail('VALIDATION', `${label}은(는) ${min}~${max} 사이의 정수로 입력하세요.`);
  return n;
};

/** 입력 검증·정리. 이름 1~40자, 수업 소개 500자, 정원 1~100(현재 인원 이상). */
export function validateCourse(i: Partial<CourseInput>): CourseInput {
  const field = COURSE_FIELDS.includes(i.field as CourseField) ? (i.field as CourseField) : fail('VALIDATION', '분야를 고르세요.');
  const mode = COURSE_MODES.includes(i.mode as CourseMode) ? (i.mode as CourseMode) : 'GROUP';
  const capacity = int(i.capacity, 1, COURSE_LIMITS.capacityMax, '정원');
  const enrolled = int(i.enrolled ?? 0, 0, COURSE_LIMITS.capacityMax, '현재 인원');
  if (enrolled > capacity) fail('VALIDATION', '정원은 현재 인원 이상이어야 합니다.');
  return {
    name: text(i.name, COURSE_LIMITS.name, '수업 이름', true), field, mode,
    teacher: text(i.teacher, COURSE_LIMITS.teacher, '담당 선생님', true),
    audience: text(i.audience, COURSE_LIMITS.audience, '대상'),
    schedule: text(i.schedule, COURSE_LIMITS.schedule, '일정'),
    summary: text(i.summary, COURSE_LIMITS.summary, '한 줄 소개'),
    description: text(i.description, COURSE_LIMITS.description, '수업 소개'),
    materials: text(i.materials, COURSE_LIMITS.materials, '준비물'),
    capacity, enrolled, published: Boolean(i.published), signupOpen: Boolean(i.signupOpen),
    fee: i.fee === undefined ? undefined : int(i.fee, 0, COURSE_LIMITS.feeMax, '회당 수업료'),
    ...(typeof i.programId === 'string' && i.programId ? { programId: i.programId } : {}),
  };
}

/** 새 수업은 항상 준비 중(비공개)으로 만든다. */
export function newCourse(i: { id: string; centerId: string; input: Partial<CourseInput>; by: string; now: string }): CenterCourse {
  const v = validateCourse(i.input);
  return { id: i.id, centerId: i.centerId, ...v, published: false, goals: DEFAULT_COURSE_GOALS[v.field], version: 1, createdAt: i.now, updatedAt: i.now, updatedBy: i.by };
}

export function updateCourse(cur: CenterCourse, input: Partial<CourseInput>, by: string, now: string): CenterCourse {
  const v = validateCourse(input);
  const goals = v.field === cur.field ? cur.goals : DEFAULT_COURSE_GOALS[v.field];
  // 프로그램을 '없음'으로 바꾸면 연결도 지운다
  const { programId: _old, ...rest } = cur;
  return { ...rest, ...v, goals, version: cur.version + 1, updatedAt: now, updatedBy: by };
}

/** 보이는 순서: 원장이 정한 순서(sortOrder) → 정하지 않은 수업은 만든 순서로 그 뒤에 */
export function sortCourses<T extends Pick<CenterCourse, 'sortOrder' | 'createdAt'>>(list: readonly T[]): T[] {
  const key = (c: T) => c.sortOrder ?? Number.MAX_SAFE_INTEGER;
  return [...list].sort((a, b) => key(a) - key(b) || a.createdAt.localeCompare(b.createdAt));
}

/**
 * 순서 바꾸기: ids 는 센터의 모든 수업 id 를 새 순서대로 담아야 한다(그사이 수업이 생기거나 지워졌으면 거절).
 * 순서가 실제로 바뀐 수업만 돌려준다.
 */
export function reorderCourses(list: readonly CenterCourse[], ids: readonly string[], by: string, now: string): CenterCourse[] {
  const byId = new Map(list.map((c) => [c.id, c]));
  if (new Set(ids).size !== ids.length || ids.length !== list.length || ids.some((id) => !byId.has(id))) {
    fail('CONFLICT', '그사이 수업이 추가되거나 바뀌었습니다. 새로고침한 뒤 다시 정렬하세요.');
  }
  return ids.flatMap((id, i) => {
    const c = byId.get(id)!;
    return c.sortOrder === i ? [] : [{ ...c, sortOrder: i, version: c.version + 1, updatedAt: now, updatedBy: by }];
  });
}

/** 모집 상태: 정원이 차면 마감 */
export const courseFull = (c: Pick<CenterCourse, 'capacity' | 'enrolled'>) => c.enrolled >= c.capacity;
export const courseRemaining = (c: Pick<CenterCourse, 'capacity' | 'enrolled'>) => Math.max(0, c.capacity - c.enrolled);

/** 보호자에게 보낼 때: 공개 수업만, 내부 기록(작성자) 제외 */
export function publicCourses(list: readonly CenterCourse[]) {
  return list.filter((c) => c.published).map(({ updatedBy: _u, ...rest }) => rest);
}

/* ---------------- 보호자 수강 신청 ---------------- */

/** PENDING 검토 중 · ACCEPTED 수락 · DECLINED 거절 · CANCELLED 보호자 취소 · ENDED 수강 종료 */
export type CourseRequestStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'CANCELLED' | 'ENDED';
/** 자녀 한 명이 동시에 검토를 기다릴 수 있는 신청 수 */
export const COURSE_PENDING_LIMIT = 5;

/** 수강 신청. 수업(CenterCourse)에는 아이 정보를 넣지 않고 여기에만 둔다. 원장에게만 아이 이름과 함께 보인다. */
export interface CourseRequest {
  id: string;
  centerId: string;
  courseId: string;
  clientId: string;     // 자녀와 연결된 센터 이용자
  childId: string;
  guardianId: string;
  note?: string;        // 센터에 전할 말
  status: CourseRequestStatus;
  createdAt: string;
  decidedAt?: string;
  decidedBy?: string;
  reason?: string;      // 거절 사유(보호자에게 보인다)
  version: number;
}

const activeFor = (list: readonly CourseRequest[], clientId: string, courseId: string) =>
  list.find((r) => r.clientId === clientId && r.courseId === courseId && (r.status === 'PENDING' || r.status === 'ACCEPTED'));

/** 지금 보호자가 신청할 수 있는 수업인가: 공개 + 신청 받는 중 + 자리 있음 */
export const courseSignupOpen = (c: Pick<CenterCourse, 'published' | 'signupOpen' | 'capacity' | 'enrolled'>) => c.published && Boolean(c.signupOpen) && !courseFull(c);

export function newCourseRequest(
  i: { id: string; course: CenterCourse; clientId: string; childId: string; guardianId: string; note?: string; now: string },
  existing: readonly CourseRequest[],
): CourseRequest {
  if (!i.course.published) fail('NOT_FOUND', '수업을 찾을 수 없습니다.');
  const same = activeFor(existing, i.clientId, i.course.id);
  if (same) fail('CONFLICT', same.status === 'ACCEPTED' ? '이미 수강이 확정된 수업입니다.' : '이미 신청한 수업입니다. 센터의 확인을 기다려 주세요.');
  if (!courseSignupOpen(i.course)) fail('CONFLICT', courseFull(i.course) ? '정원이 마감된 수업입니다.' : '지금은 신청을 받지 않는 수업입니다.');
  const waiting = existing.filter((r) => r.clientId === i.clientId && r.status === 'PENDING').length;
  if (waiting >= COURSE_PENDING_LIMIT) fail('CONFLICT', `검토를 기다리는 신청은 자녀당 ${COURSE_PENDING_LIMIT}개까지입니다.`);
  const note = text(i.note, COURSE_LIMITS.note, '센터에 전할 말') || undefined;
  return { id: i.id, centerId: i.course.centerId, courseId: i.course.id, clientId: i.clientId, childId: i.childId, guardianId: i.guardianId, note, status: 'PENDING', createdAt: i.now, version: 1 };
}

/** 보호자 취소: 검토 중인 신청만. 수강이 확정되면 센터에 말해야 한다. */
export function cancelCourseRequest(r: CourseRequest, guardianId: string, now: string): CourseRequest {
  if (r.guardianId !== guardianId) fail('NOT_FOUND', '신청을 찾을 수 없습니다.');
  if (r.status !== 'PENDING') fail('CONFLICT', r.status === 'ACCEPTED' ? '수강이 확정된 수업은 센터에 말씀해 주세요.' : '이미 처리된 신청입니다.');
  return { ...r, status: 'CANCELLED', decidedAt: now, decidedBy: guardianId, version: r.version + 1 };
}

/**
 * 원장의 결정. 수락하면 현재 인원 +1(자리가 없으면 거부).
 * 돌려준 신청과 수업은 저장소가 한 번에(버전 조건으로) 저장해야 정원을 넘지 않는다.
 */
export function decideCourseRequest(
  r: CourseRequest, course: CenterCourse, accept: boolean, by: string, now: string, reason: string | undefined, existing: readonly CourseRequest[],
): { request: CourseRequest; course?: CenterCourse } {
  if (r.courseId !== course.id || r.centerId !== course.centerId) fail('NOT_FOUND', '신청을 찾을 수 없습니다.');
  if (r.status !== 'PENDING') fail('CONFLICT', '이미 처리된 신청입니다.');
  const done = { decidedAt: now, decidedBy: by, version: r.version + 1 };
  if (!accept) return { request: { ...r, ...done, status: 'DECLINED', reason: text(reason, COURSE_LIMITS.reason, '거절 사유') || undefined } };
  if (courseFull(course)) fail('CONFLICT', '정원이 찼습니다. 정원을 늘린 뒤 수락하세요.');
  if (existing.some((x) => x.id !== r.id && x.clientId === r.clientId && x.courseId === r.courseId && x.status === 'ACCEPTED')) fail('CONFLICT', '이미 수강이 확정된 아이입니다.');
  return {
    request: { ...r, ...done, status: 'ACCEPTED' },
    course: { ...course, enrolled: course.enrolled + 1, version: course.version + 1, updatedAt: now, updatedBy: by },
  };
}

/** 수강 종료(원장): 현재 인원 -1. 이후 다시 신청할 수 있다. */
export function endCourseRequest(r: CourseRequest, course: CenterCourse, by: string, now: string): { request: CourseRequest; course: CenterCourse } {
  if (r.courseId !== course.id || r.centerId !== course.centerId) fail('NOT_FOUND', '신청을 찾을 수 없습니다.');
  if (r.status !== 'ACCEPTED') fail('CONFLICT', '수강 중인 신청만 종료할 수 있습니다.');
  return {
    request: { ...r, status: 'ENDED', decidedAt: now, decidedBy: by, version: r.version + 1 },
    course: { ...course, enrolled: Math.max(0, course.enrolled - 1), version: course.version + 1, updatedAt: now, updatedBy: by },
  };
}

/** 12회기 커리큘럼 구성 예시(담당 선생님이 검토할 초안) */
export const CURRICULUM_TEMPLATE: ReadonlyArray<{ topic: string; activity: string; check: string }> = [
  { topic: '만나기', activity: '수업 공간과 활동 알아보기', check: '관심과 참여 방식 살펴보기' },
  { topic: '탐색하기', activity: '다양한 재료와 활동 경험하기', check: '선호 활동 기록하기' },
  { topic: '표현하기', activity: '나의 선택과 생각 표현하기', check: '표현 방법 살펴보기' },
  { topic: '주고받기', activity: '순서를 정해 함께 활동하기', check: '차례와 반응 확인하기' },
  { topic: '연결하기', activity: '익숙한 활동을 새롭게 이어 보기', check: '참여 변화 기록하기' },
  { topic: '중간 돌아보기', activity: '앞선 활동을 돌아보고 이야기하기', check: '후반 수업 목표 조정하기' },
  { topic: '확장하기', activity: '새로운 주제와 도구 만나기', check: '새 활동에 대한 반응 살펴보기' },
  { topic: '함께 해결하기', activity: '작은 과제를 함께 완성하기', check: '도움 요청과 협력 살펴보기' },
  { topic: '스스로 해보기', activity: '활동 순서를 정하고 실행하기', check: '스스로 시도한 부분 기록하기' },
  { topic: '일상과 잇기', activity: '일상에서 만나는 상황으로 연습하기', check: '가정 연계 아이디어 나누기' },
  { topic: '나의 활동 만들기', activity: '선호 활동을 직접 구성하기', check: '선택과 참여 과정 돌아보기' },
  { topic: '마무리와 다음 단계', activity: '활동 기록을 함께 정리하기', check: '변화와 다음 목표 나누기' },
];
